import type RpcDataPagination from '@/core/services/Rpc/RpcDataPagination';
import type RpcDataSort       from '@/core/services/Rpc/RpcDataSort';
import Core                   from '@/core/Core';
import Assembly               from '@/domains/Assemblies/Assembly';
import AssembliesDAO          from '@/domains/Assemblies/AssembliesDAO';
import ShipmentsService       from '@/domains/Shipments/ShipmentsService';
import TransportOrdersService from "@/domains/TransportOrders/TransportOrdersService";
import ContextMenuItem        from '@/lib/components/VContextMenu/ContextMenuItem';
import OrderPosition          from "@/domains/Orders/OrderPosition";
import CriticalValidationError from "@/core/misc/CriticalValidationError";

class AssembliesService {

	/**
	 * Загрузить одну сборку по ID
	 */
	static async get(id: number): Promise<Assembly | null> {
		const response = await AssembliesDAO.get(id, Core.currentUser.isAdmin);

		return Core.utils.hydrate(response.data, Assembly.fromServer);
	}

	/**
	 * Загрузить список сборок с пагинацией.
	 * Используется в реестре через DataGridLoader.
	 */
	static async getListPaginated(
		params?: Parameters<typeof AssembliesDAO.getList>[0] & {
			pagination?: RpcDataPagination;
			sort?: RpcDataSort;
		}
	): Promise<{ data: Assembly[]; pagination: RpcDataPagination }> {
		const { pagination, sort, ...filters } = params ?? {};
		const response = await AssembliesDAO.getList(filters, pagination, sort);

		return {
			data:       Core.utils.hydrateArray(response.data, Assembly.fromServer),
			pagination: response.meta.pagination,
		};
	}

	/**
	 * Загрузить список сборок.
	 * Для использования вне реестра (диалоги, автокомплиты).
	 */
	static async getList(
		params?: Parameters<typeof AssembliesDAO.getList>[0]
	): Promise<Assembly[]> {
		return (await this.getListPaginated(params)).data;
	}

	/**
	 * Создать сборочный лист
	 */
	static async add(assembly: Assembly): Promise<boolean> {
		const response = await AssembliesDAO.add(assembly);

		if (response.ok) {
			Core.showNotification.success('Сборочный лист создан');
		}

		return response.ok;
	}

	/**
	 * Обновить сборочный лист
	 */
	static async update(assembly: Assembly, changeReasonText?: string): Promise<boolean> {
		const response = await AssembliesDAO.update(assembly, changeReasonText);

		if (response.ok) {
			Core.showNotification.success('Сборочный лист обновлён');
		}

		return response.ok;
	}

	/**
	 * Удалить сборочный лист
	 */
	static async delete(assembly: Assembly, changeReasonText?: string): Promise<boolean> {
		const response = await AssembliesDAO.delete(assembly, changeReasonText);

		if (response.ok) {
			Core.showNotification.success('Сборочный лист удалён');
		}

		return response.ok;
	}

	/**
	 * Удалить сборочный лист с подтверждением.
	 * Возвращает true если удаление произошло — вызывающий сам решает что делать
	 * (обычно refresh лоадера регистра).
	 */
	static async deleteWithConfirm(assembly: Assembly): Promise<boolean> {
		const confirmed = await Core.confirm('Подтверждаете удаление сборки?');

		if (confirmed === true) {
			const isDeleted = await this.delete(assembly);

			if (isDeleted === true) {
				assembly.isDeleted = true;
			}

			return isDeleted;
		} else {
			return false;
		}
	}

	/**
	 * Показывает карточку сборки.
	 * source — то, из чего сборка берётся: существующая сборка (открываем по id),
	 * либо позиции заказа (создаём новую).
	 *
	 * Промис резолвится по закрытию карточки: true — сохранили, false — закрыли.
	 */
	static showAssemblyCard(source: Assembly | OrderPosition | OrderPosition[]): Promise<boolean> {
		if (source instanceof Assembly) {
			return Core.dialogs.AssemblyCard.showAsPromise({ id: source.id });
		}
		else if (Array.isArray(source) && source[0] instanceof OrderPosition) {
			return Core.dialogs.AssemblyCard.showAsPromise({ orderPositions: source });
		}
		else if (source instanceof OrderPosition) {
			return Core.dialogs.AssemblyCard.showAsPromise({ orderPositions: [source] });
		}
		else {
			throw new CriticalValidationError(
				'AssembliesService.showAssemblyCard: неизвестный источник сборки',
				{ source },
			);
		}
	}

	/** Поповер с доставкой и температурным режимом сборки (клик по info в реестрах) */
	static showAssemblyInfoPopup(assembly: Assembly, target: HTMLElement): void {
		Core.dialogs.AssemblyInfoPopup.hideIfOpen();
		Core.dialogs.AssemblyInfoPopup.show({ assembly, target });
	}

	// ============================================================
	// Контекстное меню
	// ============================================================

	/**
	 * Пункты контекстного меню сборки для грида реестра.
	 * Передаётся в VDataGridV2 пропом :context-menu — грид сам рисует
	 * кебаб-колонку и меню по правому клику, и обновляет список после
	 * действия, только если callback пункта вернул true.
	 *
	 * Геттер, а не статическое свойство: «Создать ЗТК» зависит от
	 * Core.currentPartner, недоступного на этапе загрузки модуля.
	 *
	 * callback возвращает промис — грид дождётся завершения перед refresh.
	 */
	static get contextMenu(): ContextMenuItem[] {
		return [
			new ContextMenuItem(
				'Открыть',
				'open_in_new',
				(assembly: Assembly) => { return this.showAssemblyCard(assembly); },
				null,
				false,  // hidden
				true,   // dividerAfter
			),
			new ContextMenuItem(
				'Отгрузить',
				'local_shipping',
				(assembly: Assembly) => { return ShipmentsService.showShipmentCard(assembly); },
				(assembly: Assembly) => { return Assembly.reasonUnavailableShipmentString(assembly)?.string ?? null; },
			),
			new ContextMenuItem(
				'Создать ЗТК',
				'add_circle_outline',
				(assembly: Assembly) => { return TransportOrdersService.showSingleSenderTransportOrderDialog(assembly); },
				(assembly: Assembly) => { return Assembly.reasonUnavailableCreateTransportOrderString(assembly) ?? null; },
				() => { return Core.currentPartner.isSeller === true; },  // hidden
			),
			new ContextMenuItem(
				'История действий',
				'history',
				(assembly: Assembly) => { Core.dialogs.FeedDialog.show({ entityTypeName: 'assembly', entityId: assembly?.id, showAddComment: true }); },
				(assembly: Assembly) => { return Assembly.reasonUnavailableHistory(assembly)?.string ?? null; },
				false,  // hidden
			),
			new ContextMenuItem(
				'Удалить',
				'delete',
				(assembly: Assembly) => { return this.deleteWithConfirm(assembly); },
				(assembly: Assembly) => { return Assembly.reasonUnavailableDelete(assembly) ?? null; },
			),
		];
	}
}

export default AssembliesService;